import { locationSurchargeType } from "./location-surcharge-type.const";
import { LocationSurchargeBase } from "./location-surcharge-base.model";
import { LocationSurcharge } from "./location-surcharge.type";
import { LocationSurchargeMapper } from "./location-surcharge.mapper";
import { Carrier } from "../../carrier/carrier.model";
import { LclQuoteLocationSurcharge } from "../../quote/quote-surcharge/models/quote-location-surcharge-lcl.model";
import { AirQuote } from "../../quote/models/quote-air.model";
import { AirQuoteLocationSurcharge } from "../../quote/quote-surcharge/models/quote-location-surcharge-air.model";
import { CurrencyRoundPipe } from "../../shared/currencies/currency-round.pipe";
import { SurchargeTypeMapper } from "app/surcharge-type/surcharge-type.mapper";
import { SurchargeType } from "app/surcharge-type/surcharge-type.model";

export class AirLocationSurcharge extends LocationSurchargeBase {
  constructor(
    public id: number,
    public discriminator: string,
    public name: string = "",
    protected carrier: Carrier,
    protected type: string = locationSurchargeType.destination,
    protected weightPrice: number = 0,
    protected chargeableWeightPrice: number = 0,
    protected measurementPrice: number = 0,
    protected shipmentPrice: number = 0,
    protected piecePrice: number = 0,
    protected documentPrice: number = 0,
    protected minPrice: number = 0,
    protected maxPrice: number = 0,
    protected assignment: string = "",
    protected quoteLocationSurcharges: LclQuoteLocationSurcharge[],
    public currency: string = "USD",
    public surchargeType: SurchargeType,
    public debugInfo?: string,
    public chargedFor?: string,
    public internalNote?: string,
    public availableOnline?: boolean,
    public sort?: number,
  ) {
    super(
      id,
      discriminator,
      name,
      carrier,
      type,
      assignment,
      quoteLocationSurcharges,
      currency,
      surchargeType,
      debugInfo,
      chargedFor,
      internalNote,
      sort,
    );
  }

  public getWeightPrice(): number {
    return this.weightPrice;
  }
  public setWeightPrice(param: number): void {
    this.weightPrice = param;
  }

  public getChargeableWeightPrice(): number {
    return this.chargeableWeightPrice;
  }
  public setChargeableWeightPrice(param: number): void {
    this.chargeableWeightPrice = param;
  }

  public getMeasurementPrice(): number {
    return this.measurementPrice;
  }
  public setMeasurementPrice(param: number) {
    this.measurementPrice = param;
  }

  public getShipmentPrice(): number {
    return +this.shipmentPrice;
  }
  public setShipmentPrice(param: number) {
    this.shipmentPrice = param;
  }

  public getMinPrice(): number {
    return this.minPrice;
  }
  public setMinPrice(param: number) {
    this.minPrice = param;
  }

  public getPiecePrice(): number {
    return this.piecePrice;
  }
  public setPiecePrice(param: number) {
    this.piecePrice = param;
  }

  public getDocumentPrice(): number {
    return this.documentPrice;
  }
  public setDocumentPrice(param: number) {
    this.documentPrice = param;
  }

  public getMaxPrice(): number {
    return this.maxPrice;
  }
  public setMaxPrice(param: number) {
    this.maxPrice = param;
  }

  // TODO: This Logic should be moved to the API !!
  public get total(): number {
    if (!this.haveQuoteLocationSurcharges()) {
      return 0;
    }
    const quoteLocationSurcharge: any = this.getFirstQuoteLocationSurcharge();
    const quote = quoteLocationSurcharge.getQuote();

    if (
      quote instanceof AirQuote &&
      quoteLocationSurcharge instanceof AirQuoteLocationSurcharge
    ) {
      let freight = 0;

      // 1. Add bigger value (Gross, or Chargeable)
      freight += Math.max(
        (Number(this.getWeightPrice()) +
          Number(quoteLocationSurcharge.getWeightPrice())) *
          Number(quote.getWeight()),
        (Number(this.getChargeableWeightPrice()) +
          Number(quoteLocationSurcharge.getChargeableWeightPrice())) *
          Number(quote.getChargeableWeight()),
      );

      // 3. Add B / L
      freight +=
        Number(this.getShipmentPrice()) +
        Number(quoteLocationSurcharge.getShipmentPrice());

      // 4. Add Piece Price
      freight +=
        (Number(this.getPiecePrice()) +
          Number(quoteLocationSurcharge.getPiecePrice())) *
        Number(quote.getPieces());

      // 5. Add Document Price
      freight +=
        Number(this.getDocumentPrice()) +
        Number(quoteLocationSurcharge.getDocumentPrice());

      // 6. Apply Min and Max
      freight = this.getMaxPrice()
        ? Math.min(freight, this.getMaxPrice())
        : freight;
      freight = this.getMinPrice()
        ? Math.max(freight, this.getMinPrice())
        : freight;

      return new CurrencyRoundPipe().transform(freight);
    }

    return 0;
  }

  public getFirstQuoteLocationSurcharge() {
    if (this.haveQuoteLocationSurcharges()) {
      return this.quoteLocationSurcharges[0];
    }
  }

  public getRequestParams() {
    return {
      weightPrice: this.getWeightPrice(),
      // measurementPrice: this.getMeasurementPrice(),
      chargeableWeightPrice: this.getChargeableWeightPrice(),
      minPrice: this.getMinPrice(),
      maxPrice: this.getMaxPrice(),
      shipmentPrice: this.getShipmentPrice(),
      piecePrice: this.getPiecePrice(),
      documentPrice: this.getDocumentPrice(),
      carrier: this.getCarrier() ? this.getCarrier().getId() : null,
      name: this.getName(),
      type: this.getType(),
      sort: this.sort,
      availableOnline: this.availableOnline,
      surchargeType: this.getSurchargeType()
        ? this.getSurchargeType().getId()
        : null,
    };
  }

  public getClone(): LocationSurcharge {
    return LocationSurchargeMapper.map({
      id: this.id,
      discriminator: this.discriminator,
      name: this.name,
      carrier: this.carrier,
      type: this.type,
      weightPrice: this.getWeightPrice(),
      chargeableWeightPrice: this.getChargeableWeightPrice(),
      measurementPrice: this.getMeasurementPrice(),
      shipmentPrice: this.shipmentPrice,
      piecePrice: this.piecePrice,
      documentPrice: this.documentPrice,
      minPrice: this.minPrice,
      maxPrice: this.maxPrice,
      assignment: this.assignment,
      quoteLocationSurcharges: this.quoteLocationSurcharges,
      currency: this.currency,
      sort: this.sort,
      availableOnline: this.availableOnline,
      chargedFor: this.chargedFor,
      debugInfo: this.debugInfo,
      surchargeType: SurchargeTypeMapper.map(this.getSurchargeType()),
    });
  }
}
